import React, { useEffect, useState } from 'react';

/* ─────────────────────────────────────────────────────────
 * SELECTION ACTIONS
 * Highlight a passage and hand it to the agent to rewrite,
 * explain, find bugs, or summarize.
 * ───────────────────────────────────────────────────────── */

export default function SelectionToolbar({
  onAction,
}: {
  onAction?: (action: string, selectedText: string) => void;
}) {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedText, setSelectedText] = useState('');

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        setPosition(null);
        setSelectedText('');
        return;
      }

      const text = selection.toString().trim();
      if (text.length < 3) {
        setPosition(null);
        setSelectedText('');
        return;
      }

      try {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setSelectedText(text);
          setPosition({
            x: rect.left + rect.width / 2,
            y: rect.top - 10,
          });
        }
      } catch {
        setPosition(null);
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  if (!position || !selectedText) return null;

  const trigger = (action: string) => {
    if (onAction) onAction(action, selectedText);
    setPosition(null);
    setSelectedText('');
    window.getSelection()?.removeAllRanges();
  };

  return (
    <div
      className="fixed z-50 -translate-x-1/2 -translate-y-full flex items-center gap-1 rounded-full bg-surface shadow-raised border border-line-strong px-2 py-1 backdrop-blur-md"
      style={{ left: position.x, top: position.y }}
    >
      <button
        type="button"
        onClick={() => trigger('rewrite')}
        className="px-2 py-1 rounded-full text-xs font-medium text-accent hover:bg-hover transition-colors"
      >
        🪄 Rewrite
      </button>
      <button
        type="button"
        onClick={() => trigger('explain')}
        className="px-2 py-1 rounded-full text-xs font-medium text-ink-2 hover:text-ink hover:bg-hover transition-colors"
      >
        💡 Explain
      </button>
      <button
        type="button"
        onClick={() => trigger('bugs')}
        className="px-2 py-1 rounded-full text-xs font-medium text-orange hover:bg-hover transition-colors"
      >
        🔍 Find Bugs
      </button>
      <span className="w-px h-3 bg-line" />
      <button
        type="button"
        onClick={() => trigger('summarize')}
        className="px-2 py-1 rounded-full text-xs font-medium text-ink-3 hover:text-ink hover:bg-hover transition-colors"
      >
        📝 Summarize
      </button>
    </div>
  );
}
